import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { map, catchError, switchMap, tap, take, filter } from 'rxjs/operators';
import { apiUrl } from '../../../environments/environment';
import { BYPASS_AUTH } from './http-context-tokens';

export type UserRole = 'Admin' | 'Tester' | 'Developer' | 'Manager' | 'QA Analyst';

export interface UserData {
  uid: string;
  role: UserRole;
  assignedProjects: string[];
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private loggedIn = new BehaviorSubject<boolean>(false);
  public isLoggedIn$: Observable<boolean> = this.loggedIn.asObservable();

  private userRole = new BehaviorSubject<UserRole | null>(null);
  public userRole$ = this.userRole.asObservable();

  private email = new BehaviorSubject<string>('');
  email$ = this.email.asObservable();

  private ruolo = new BehaviorSubject<string>('');
  ruolo$ = this.ruolo.asObservable();

  private tokenSubject = new BehaviorSubject<string | null>(null);
  public token$ = this.tokenSubject.asObservable();

  private isRefreshing = false;
  private refreshedToken$ = new Subject<string | null>();

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {
  }

  public initializeAuthState(): Promise<void> {
    return new Promise((resolve) => {
      const accessToken = localStorage.getItem('access_token');
      if (!accessToken) {
        this._clearState();
        resolve();
        return;
      }

      // Check if token is expired
      const payload = this._decodePayload(accessToken);
      if (!payload || this._isExpired(payload.exp)) {
        // Try refresh
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          this._refreshToken(refreshToken).subscribe({
            next: (newToken) => {
              this._loadUserProfile(newToken).then(() => resolve());
            },
            error: () => {
              this._clearState();
              resolve();
            }
          });
        } else {
          this._clearState();
          resolve();
        }
        return;
      }

      this.tokenSubject.next(accessToken);
      this._loadUserProfile(accessToken).then(() => resolve());
    });
  }

  private _loadUserProfile(token: string): Promise<void> {
    return new Promise((resolve) => {
      const payload = this._decodePayload(token);
      if (payload) {
        this.loggedIn.next(true);
        this.email.next(payload.email || '');
        this.tokenSubject.next(token);
        if (payload.role) {
          this.userRole.next(payload.role as UserRole);
          this.ruolo.next(payload.role);
        }
      }
      resolve();
    });
  }

  getToken(): string | null {
    return localStorage.getItem('access_token');
  }

  getFreshToken(): Observable<string | null> {
    const token = localStorage.getItem('access_token');
    if (!token) {
      return of(null);
    }

    const payload = this._decodePayload(token);
    // Refresh if expiring within 5 minutes
    if (payload && this._isExpiringSoon(payload.exp, 300)) {
      if (this.isRefreshing) {
        return this.refreshedToken$.pipe(take(1));
      }

      this.isRefreshing = true;
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        return of(null);
      }

      return this._refreshToken(refreshToken).pipe(
        tap(newToken => {
          this.isRefreshing = false;
          this.refreshedToken$.next(newToken);
        }),
        catchError(() => {
          this.isRefreshing = false;
          this.refreshedToken$.next(null);
          return of(null);
        })
      );
    }

    return of(token);
  }

  async signinUser(email: string, password: string): Promise<boolean> {
    try {
      const response = await this.http.post<any>(
        `${apiUrl}/api/auth/login`,
        { email, password },
        { context: new HttpContext().set(BYPASS_AUTH, true) }
      ).toPromise();

      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);

      this.loggedIn.next(true);
      this.email.next(response.email);
      this.tokenSubject.next(response.access_token);
      this.userRole.next(response.role as UserRole);
      this.ruolo.next(response.role);

      return true;
    } catch (error) {
      console.error('Login failed:', error);
      this.loggedIn.next(false);
      throw error;
    }
  }

  async logout(): Promise<void> {
    this._clearState();
    this.router.navigate(['/pages/login']);
  }

  isAuthenticated(): boolean {
    return this.loggedIn.value;
  }

  hasRole(role: UserRole): Observable<boolean> {
    return this.userRole$.pipe(
      map(userRole => userRole === role)
    );
  }

  async setUserRole(uid: string, role: UserRole): Promise<void> {
    await this.http.put(`${apiUrl}/api/auth/users/${uid}/role`, { role }).toPromise();
  }

  async deleteUser(email: string): Promise<void> {
    await this.http.delete(`${apiUrl}/api/auth/users/${email}`).toPromise();
  }

  async createUser(email: string, password: string, role: UserRole = 'Tester'): Promise<void> {
    await this.http.post(`${apiUrl}/api/auth/register`, { email, password, role }).toPromise();
  }

  getAllUsers(): Observable<UserData[]> {
    return this.http.get<UserData[]>(`${apiUrl}/api/auth/users`);
  }

  getCurrentUser(): Promise<any> {
    const payload = this._decodePayload(localStorage.getItem('access_token') || '');
    return Promise.resolve(payload ? { email: payload.email, uid: payload.email } : null);
  }

  getUserData(userId: string): Observable<UserData | null> {
    return this.http.get<UserData>(`${apiUrl}/api/auth/users/${userId}`).pipe(
      catchError(() => of(null))
    );
  }

  getCurrentUserData(): Observable<UserData | null> {
    if (!this.loggedIn.value) {
      return of(null);
    }
    return this.http.get<UserData>(`${apiUrl}/api/auth/me`).pipe(
      catchError(() => of(null))
    );
  }

  updateUserDataService() {
    this.getCurrentUserData().subscribe((userData: UserData | null) => {
      if (userData) {
        this.email.next(userData.uid);
        this.ruolo.next(userData.role);
      } else {
        console.log('DEBUG No user data available : navbar.component.TS');
      }
    });
  }

  assignProjectToUser(userId: string, projectId: string): Promise<void> {
    return this.http.put<any>(`${apiUrl}/api/auth/users/${userId}/projects/assign`, { projectId }).toPromise();
  }

  removeProjectFromUser(userId: string, projectId: string): Promise<void> {
    return this.http.put<any>(`${apiUrl}/api/auth/users/${userId}/projects/remove`, { projectId }).toPromise();
  }

  getUserAssignedProjects(userId: string): Observable<string[]> {
    return this.http.get<string[]>(`${apiUrl}/api/auth/users/${userId}/projects`);
  }

  // ── Private helpers ──────────────────────────────────────────

  private _clearState() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token');
    this.loggedIn.next(false);
    this.email.next('');
    this.userRole.next(null);
    this.ruolo.next('');
    this.tokenSubject.next(null);
  }

  private _decodePayload(token: string): any {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      return JSON.parse(atob(parts[1]));
    } catch {
      return null;
    }
  }

  private _isExpired(exp: number): boolean {
    return Date.now() >= exp * 1000;
  }

  private _isExpiringSoon(exp: number, withinSeconds: number): boolean {
    return Date.now() >= (exp * 1000) - (withinSeconds * 1000);
  }

  private _refreshToken(refreshToken: string): Observable<string> {
    return this.http.post<any>(
      `${apiUrl}/api/auth/refresh`,
      { refresh_token: refreshToken },
      { context: new HttpContext().set(BYPASS_AUTH, true) }
    ).pipe(
      map(response => {
        localStorage.setItem('access_token', response.access_token);
        this.tokenSubject.next(response.access_token);
        return response.access_token;
      })
    );
  }
}
